import { IChart } from "Interfaces";
import { useSelectedProject } from "Hooks/useSelectedProject";
import { parseChartFromResponse } from "features/chartLibrary/utils";
import { getStandardChart } from "features/chartLibrary/services/chartService";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import produce from "immer";

type StandardChartsMap = Record<string, IChart>;
type LoadingMap = Record<string, boolean>;

interface StandardChartsContextType {
  charts: StandardChartsMap;
  fetchChart: (chartKey: string, refresh?: boolean) => Promise<void>;
  setChart: (chartKey: string, chart: IChart) => void;
  isLoading: (chartKey: string) => boolean;
  refresh: (chartKey: string) => Promise<void>;
  clearChart: (chartKey: string) => void;
}

const StandardChartsContext = createContext<StandardChartsContextType>({
  charts: {},
  fetchChart: async () => {},
  setChart: () => {},
  isLoading: () => false,
  refresh: async () => {},
  clearChart: () => {},
});

export const useStandardCharts = () => useContext(StandardChartsContext);

export const useStandardChart = (chartKey: string) => {
  const { charts, fetchChart, setChart, isLoading, clearChart } = useContext(
    StandardChartsContext,
  );

  useEffect(() => {
    fetchChart(chartKey);
  }, [chartKey, fetchChart]);

  const refresh = useCallback(
    () => fetchChart(chartKey, true),
    [fetchChart, chartKey],
  );

  return useMemo(
    () => ({
      chart: charts[chartKey] ?? null,
      loading: isLoading(chartKey),
      setChart: (chart: IChart) => setChart(chartKey, chart),
      refresh,
      clearChart: () => clearChart(chartKey),
    }),
    [charts, chartKey, setChart, isLoading, refresh, clearChart],
  );
};

const StandardChartsProvider = ({ children }: { children: ReactNode }) => {
  const { selectedProject } = useSelectedProject();
  const [charts, setCharts] = useState<StandardChartsMap>({});
  const [loading, setLoading] = useState<LoadingMap>({});

  const fetchChart = useCallback(
    async (chartKey: string, refresh = false) => {
      if (!selectedProject?.id) return;
      if (!refresh && charts[chartKey]) return;

      setLoading((prev) => ({ ...prev, [chartKey]: true }));
      try {
        const res = await getStandardChart(
          selectedProject.id as number,
          chartKey,
        );
        if (res?.chartKey === chartKey) {
          setCharts((prev) => ({
            ...prev,
            [chartKey]: parseChartFromResponse(res),
          }));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading((prev) => ({ ...prev, [chartKey]: false }));
      }
    },
    [selectedProject?.id, charts],
  );

  const setChart = useCallback((chartKey: string, chart: IChart) => {
    setCharts((prev) => ({ ...prev, [chartKey]: chart }));
  }, []);

  const clearChart = useCallback(
    (chartKey: string) => {
      setCharts(
        produce(charts, (draft) => {
          delete draft[chartKey];
        }),
      );
    },
    [charts],
  );

  const isLoading = useCallback(
    (chartKey: string) => !!loading[chartKey],
    [loading],
  );

  const contextValue = useMemo(
    () => ({
      charts,
      fetchChart,
      setChart,
      isLoading,
      clearChart,
      refresh: async () => {
        console.error("only allowed to refresh a single chart");
      },
    }),
    [charts, fetchChart, setChart, isLoading, clearChart],
  );

  return (
    <StandardChartsContext.Provider value={contextValue}>
      {children}
    </StandardChartsContext.Provider>
  );
};

export default StandardChartsProvider;
